import { useEffect, useRef, useState } from "react";
import { useMultimoneda } from "../../../shared/hooks/useMultimoneda";
import { getCuentasBancarias } from "../services/portalService";

/**
 * Campos del pago con que se aprueba una reservación: fecha, método, cuenta
 * bancaria y documento. Los usan "Nueva reservación" y "Revisar reservación".
 *
 * La cuenta bancaria es obligatoria: el recibo que se genera al aprobar entra
 * a ese banco (movimiento bancario + asiento), igual que un recibo normal. La
 * moneda del cobro la define la cuenta elegida.
 *
 * La excepción es "Sin costo": hay condominios donde las áreas comunes no se
 * cobran. Ahí no hay nada que facturar ni que depositar, así que la aprobación
 * no genera factura, ni cuenta por cobrar, ni recibo, ni asiento — solo queda
 * registrada la reserva. Si el área no tiene precio, el método arranca en
 * "Sin costo" solo.
 */

export const labelSt = { fontSize: 11, fontWeight: 700, color: "var(--text-secondary)", display: "block", marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.5 };
export const fldStyle = (extra = {}) => ({
  width: "100%", boxSizing: "border-box", padding: "9px 12px", borderRadius: 8,
  border: "1px solid var(--border-card)", background: "var(--bg-input, var(--hover-bg))",
  color: "var(--text-primary)", fontSize: 13, outline: "none", ...extra,
});

export function hoyStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export const SIN_COSTO = "SIN_COSTO";

export function pagoInicial() {
  return { fecha_pago: hoyStr(), metodo_pago: "TRANSFERENCIA", cuenta_bancaria: "", num_doc_pago: "" };
}

/** true si el área no se cobra (sin precio en ninguna de las dos monedas). */
export function areaSinPrecio(area) {
  if (!area) return false;
  return !(Number(area.precio_colones) > 0) && !(Number(area.precio_dolares) > 0);
}

/** Mensaje de error si faltan datos del pago, o "" si está completo. */
export function validarPago(pago) {
  if (pago.metodo_pago === SIN_COSTO) return "";   // no hay cobro que validar
  if (!pago.fecha_pago) return "Indicá la fecha de pago.";
  if (!pago.cuenta_bancaria) return "Seleccioná la cuenta bancaria donde entró el pago.";
  if (pago.metodo_pago !== "EFECTIVO" && !pago.num_doc_pago.trim()) return "Indicá el número de documento o referencia.";
  return "";
}

/** Agrega los datos del pago a un FormData o a un objeto JSON. */
export function pagoPayload(pago) {
  if (pago.metodo_pago === SIN_COSTO) return { metodo_pago: SIN_COSTO };
  return {
    fecha_pago: pago.fecha_pago,
    metodo_pago: pago.metodo_pago,
    cuenta_bancaria: pago.cuenta_bancaria,
    num_doc_pago: pago.num_doc_pago.trim(),
  };
}

const METODOS = [
  ["TRANSFERENCIA", "Transferencia"],
  ["EFECTIVO", "Efectivo"],
  ["CHEQUE", "Cheque"],
  [SIN_COSTO, "Sin costo (no se cobra)"],
];

function fmt(n, dec = 2) {
  return Number(n).toLocaleString("en-US", { minimumFractionDigits: dec, maximumFractionDigits: dec });
}

/** Precio que se va a cobrar según la moneda de la cuenta elegida. */
function montoACobrar(area, cuenta) {
  if (!area || !cuenta) return null;
  const usd = (cuenta.moneda || "CRC").toUpperCase() === "USD";
  if (usd) {
    return area.precio_dolares && Number(area.precio_dolares) > 0
      ? { texto: `$${fmt(area.precio_dolares)}`, ok: true }
      : { texto: "Esta cuenta es en dólares y el área no tiene precio en dólares.", ok: false };
  }
  return { texto: `₡${fmt(area.precio_colones || 0)}`, ok: true };
}

export default function DatosPagoFields({ pago, onChange, area }) {
  const multimoneda = useMultimoneda();
  const [cuentas, setCuentas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const sinCosto = pago.metodo_pago === SIN_COSTO;

  useEffect(() => {
    getCuentasBancarias()
      .then(todas => {
        // Sin multimoneda solo se ofrecen cuentas en colones (igual que en Recibos).
        const lista = multimoneda ? todas : todas.filter(c => (c.moneda || "CRC").toUpperCase() === "CRC");
        setCuentas(lista);
        if (lista.length === 1 && !pago.cuenta_bancaria) onChange({ cuenta_bancaria: String(lista[0].id) });
      })
      .catch(() => setCuentas([]))
      .finally(() => setCargando(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [multimoneda]);

  // Área sin precio → el método arranca en "Sin costo". Solo se hace una vez
  // por área y solo si el método seguía en el que viene por defecto: si el
  // administrador ya eligió otro, no se le cambia por debajo.
  const areaVista = useRef(null);
  const claveArea = area ? `${area.precio_colones}|${area.precio_dolares}` : null;
  useEffect(() => {
    if (!area || areaVista.current === claveArea) return;
    areaVista.current = claveArea;
    if (areaSinPrecio(area) && pago.metodo_pago === "TRANSFERENCIA") onChange({ metodo_pago: SIN_COSTO });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claveArea]);

  const cuenta = cuentas.find(c => String(c.id) === String(pago.cuenta_bancaria)) || null;
  const cobro  = sinCosto ? null : montoACobrar(area, cuenta);

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12 }}>
      <div>
        <label style={labelSt}>Método de pago *</label>
        <select value={pago.metodo_pago} onChange={e => onChange({ metodo_pago: e.target.value })} style={fldStyle({ cursor: "pointer" })}>
          {METODOS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>

      {sinCosto ? (
        <div style={{
          gridColumn: "1 / -1", fontSize: 12, borderRadius: 8, padding: "10px 12px",
          color: "var(--text-secondary)", background: "rgba(96,165,250,0.08)",
          border: "1px solid rgba(96,165,250,0.3)",
        }}>
          <i className="fa-solid fa-circle-info" style={{ marginRight: 6, color: "#60a5fa" }} />
          Se aprueba la reservación y <strong style={{ color: "var(--text-primary)" }}>no se cobra nada</strong>:
          no se genera factura, ni cuenta por cobrar, ni recibo, ni asiento contable.
          {areaSinPrecio(area) && " Esta área no tiene precio configurado."}
        </div>
      ) : (
        <>
          <div>
            <label style={labelSt}>Fecha de pago *</label>
            <input type="date" value={pago.fecha_pago} onChange={e => onChange({ fecha_pago: e.target.value })} style={fldStyle()} />
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={labelSt}>Cuenta bancaria *</label>
            <select value={pago.cuenta_bancaria} onChange={e => onChange({ cuenta_bancaria: e.target.value })}
              disabled={cargando} style={fldStyle({ cursor: "pointer" })}>
              <option value="">{cargando ? "Cargando cuentas…" : cuentas.length ? "Seleccioná dónde entró el pago" : "No hay cuentas bancarias registradas"}</option>
              {cuentas.map(c => (
                <option key={c.id} value={c.id}>
                  {c.banco} - {c.numero_cuenta} ({(c.moneda || "CRC").toUpperCase()})
                </option>
              ))}
            </select>
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={labelSt}>
              Número de documento {pago.metodo_pago === "EFECTIVO" ? "(opcional)" : "*"}
            </label>
            <input type="text" value={pago.num_doc_pago} onChange={e => onChange({ num_doc_pago: e.target.value })}
              placeholder={pago.metodo_pago === "CHEQUE" ? "N° de cheque" : "Referencia bancaria / N° comprobante"}
              style={fldStyle()} />
          </div>
        </>
      )}

      {cobro && (
        <div style={{
          gridColumn: "1 / -1", fontSize: 12, borderRadius: 8, padding: "8px 12px",
          color: cobro.ok ? "var(--text-secondary)" : "#f87171",
          background: cobro.ok ? "var(--hover-bg)" : "rgba(248,113,113,0.08)",
          border: `1px solid ${cobro.ok ? "var(--border-card)" : "rgba(248,113,113,0.3)"}`,
        }}>
          {cobro.ok ? (
            <>
              <i className="fa-solid fa-file-invoice-dollar" style={{ marginRight: 6, color: "#f97316" }} />
              Se genera factura y recibo por <strong style={{ color: "var(--text-primary)" }}>{cobro.texto}</strong>, con entrada en {cuenta.banco}.
            </>
          ) : (
            <><i className="fa-solid fa-triangle-exclamation" style={{ marginRight: 6 }} />{cobro.texto}</>
          )}
        </div>
      )}
    </div>
  );
}
